import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import { useLogoutAll, useMe } from "@/api/auth";
import {
  useChangePassword,
  useSessions,
  type SessionEntry,
} from "@/api/profile";
import {
  useTotpConfirm,
  useTotpDisable,
  useTotpSettings,
  useTotpSetup,
  useTotpStatus,
  type TotpSetupResponse,
} from "@/api/totp";
import { openTotpPrompt } from "@/components/TotpPrompt";
import { formatMsk } from "@/lib/datetime";
import { useT } from "@/lib/i18n";
import { useToasts } from "@/store/toasts";

export function Profile() {
  const me = useMe();
  const t = useT();

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-slate-800">{t("profile.title")}</h1>
        <p className="mt-1 text-sm text-slate-500">{t("profile.subtitle")}</p>
      </div>

      <section className="card grid gap-4 p-4 sm:grid-cols-2 sm:p-6">
        <Field label={t("profile.field.login")}>
          <span className="font-medium text-slate-800">
            {me.data?.login ?? "-"}
          </span>
        </Field>
        <Field label={t("profile.field.display_name")}>{me.data?.username ?? "-"}</Field>
        <Field label={t("profile.field.balance")}>
          {me.data ? `$${me.data.balance.toFixed(4)}` : "-"}
        </Field>
        <Field label={t("profile.field.rights")}>
          {me.data?.rights?.length
            ? me.data.rights.join(", ")
            : <span className="text-slate-400">{t("profile.field.rights.none")}</span>}
        </Field>
      </section>

      <ChangePasswordCard />
      <TwoFactorCard />
      {/* IP whitelist is admin-managed only - see Admin → Users → detail. */}
      <SessionsCard />
    </div>
  );
}

// ---- change password -----------------------------------------------------

function ChangePasswordCard() {
  const navigate = useNavigate();
  const push = useToasts((s) => s.push);
  const change = useChangePassword();
  const t = useT();

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (next !== confirm) {
      push({ kind: "error", title: t("profile.password.mismatch") });
      return;
    }
    try {
      await change.mutateAsync({
        current_password: current,
        new_password: next,
        confirm_password: confirm,
      });
      push({
        kind: "success",
        title: t("profile.password.changed"),
        body: t("profile.password.changed.body"),
      });
      navigate("/login", { replace: true });
    } catch {
      /* error rendered below */
    }
  };

  const errorMessage =
    change.error &&
    (typeof change.error.detail === "string"
      ? change.error.detail
      : t("profile.password.fail"));

  return (
    <section className="card p-4 sm:p-6">
      <h2 className="text-lg font-semibold text-slate-800">{t("profile.section.password")}</h2>
      <p className="mt-1 text-sm text-slate-500">{t("profile.section.password.subtitle")}</p>

      <form onSubmit={submit} className="mt-4 grid gap-4 sm:max-w-md">
        <div>
          <label className="label" htmlFor="current_password">
            {t("profile.password.current")}
          </label>
          <input
            id="current_password"
            type="password"
            autoComplete="current-password"
            className="input"
            required
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="new_password">
            {t("profile.password.new")}
          </label>
          <input
            id="new_password"
            type="password"
            autoComplete="new-password"
            className="input"
            required
            minLength={8}
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="confirm_password">
            {t("profile.password.confirm")}
          </label>
          <input
            id="confirm_password"
            type="password"
            autoComplete="new-password"
            className="input"
            required
            minLength={8}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>

        {errorMessage && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {errorMessage}
          </div>
        )}

        <button
          type="submit"
          className="btn btn-primary w-full sm:w-auto"
          disabled={change.isPending}
        >
          {change.isPending ? t("profile.password.busy") : t("profile.password.cta")}
        </button>
      </form>
    </section>
  );
}

// ---- active sessions -----------------------------------------------------

function SessionsCard() {
  const navigate = useNavigate();
  const push = useToasts((s) => s.push);
  const sessions = useSessions();
  const logoutAll = useLogoutAll();
  const t = useT();

  const onLogoutAll = async () => {
    try {
      const data = await logoutAll.mutateAsync();
      push({
        kind: "success",
        title: t("profile.sessions.revoked.title"),
        body: t("profile.sessions.revoked.body").replace("{n}", String(data.revoked)),
      });
      navigate("/login", { replace: true });
    } catch {
      push({ kind: "error", title: t("profile.sessions.revoke_failed") });
    }
  };

  return (
    <section className="card p-4 sm:p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">
            {t("profile.section.sessions")}
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            {t("profile.section.sessions.subtitle")}
          </p>
        </div>
        <button
          className="btn btn-ghost text-xs text-red-600"
          onClick={onLogoutAll}
          disabled={logoutAll.isPending}
        >
          {logoutAll.isPending
            ? t("profile.sessions.revoke_all.busy")
            : t("profile.sessions.revoke_all")}
        </button>
      </div>

      <div className="mt-6">
        {sessions.isLoading && (
          <div className="text-sm text-slate-400">{t("common.loading")}</div>
        )}
        {sessions.data && sessions.data.items.length === 0 && (
          <div className="text-sm text-slate-400">{t("profile.sessions.empty")}</div>
        )}
        {sessions.data && sessions.data.items.length > 0 && (
          <ul className="divide-y divide-surface-border rounded-md border border-surface-border">
            {sessions.data.items.map((s) => (
              <SessionRow key={s.sid_short} session={s} />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function SessionRow({ session }: { session: SessionEntry }) {
  const t = useT();
  return (
    <li className="flex items-center justify-between gap-3 px-4 py-3">
      <div>
        <div className="flex items-center gap-2 text-sm">
          <span className="font-mono text-slate-700">{session.ip_at_login}</span>
          {session.is_current && (
            <span className="rounded-full bg-accent-soft px-2 py-0.5 text-xs font-medium text-accent">
              {t("profile.sessions.this_device")}
            </span>
          )}
        </div>
        <div className="mt-0.5 text-xs text-slate-500">
          {t("profile.sessions.started")}{" "}
          {formatMsk(new Date(session.created_at * 1000).toISOString())}
          {" · "}
          {t("profile.sessions.last_seen")}{" "}
          {formatMsk(new Date(session.last_seen * 1000).toISOString())}
        </div>
      </div>
      <div className="font-mono text-xs text-slate-400">
        {session.sid_short}...
      </div>
    </li>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="mt-1 text-sm text-slate-800">{children}</div>
    </div>
  );
}

// ---- two-factor auth ----------------------------------------------------

function TwoFactorCard() {
  const status = useTotpStatus();
  const setup = useTotpSetup();
  const confirm = useTotpConfirm();
  const disable = useTotpDisable();
  const settings = useTotpSettings();
  const push = useToasts((s) => s.push);
  const t = useT();

  const [pending, setPending] = useState<TotpSetupResponse | null>(null);
  // Setup requires the user's current password; we cache it across
  // setup -> confirm so the form only asks once.
  const [setupPassword, setSetupPassword] = useState("");
  const [confirmCode, setConfirmCode] = useState("");
  const [disableCode, setDisableCode] = useState("");

  const enabled = !!status.data?.enabled;

  const onStart = async (e: FormEvent) => {
    e.preventDefault();
    if (!setupPassword) return;
    try {
      const r = await setup.mutateAsync({ current_password: setupPassword });
      setPending(r);
      setConfirmCode("");
    } catch (err) {
      const e = err as { detail?: unknown };
      push({
        kind: "error",
        title: t("profile.totp.setup_failed"),
        body:
          typeof e.detail === "string"
            ? e.detail
            : t("profile.totp.setup_failed.body"),
      });
    }
  };

  const onConfirm = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await confirm.mutateAsync({
        code: confirmCode,
        current_password: setupPassword,
      });
      setPending(null);
      setConfirmCode("");
      setSetupPassword("");
      push({
        kind: "success",
        title: t("profile.totp.activated"),
        body: t("profile.totp.activated.body"),
      });
    } catch (err) {
      const e = err as { detail?: unknown };
      push({
        kind: "error",
        title: t("profile.totp.wrong_code"),
        body:
          typeof e.detail === "string"
            ? e.detail
            : t("profile.totp.wrong_code.body"),
      });
    }
  };

  const onDisable = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await disable.mutateAsync({ code: disableCode });
      setDisableCode("");
      push({ kind: "info", title: t("profile.totp.disabled_toast") });
    } catch (err) {
      const e = err as { detail?: unknown };
      push({
        kind: "error",
        title: t("profile.totp.wrong_code"),
        body:
          typeof e.detail === "string"
            ? e.detail
            : t("profile.totp.cannot_disable"),
      });
    }
  };

  // PATCH /2fa/settings now demands a TOTP - protects against a
  // stolen session toggling the gate off. Prompt, retry on wrong
  // code, give up on Cancel.
  const askAndPatch = async (
    patch: {
      require_login?: boolean;
      require_purchase?: boolean;
      require_deposit_change?: boolean;
    },
    title: string,
  ): Promise<void> => {
    let errorMessage: string | undefined;
    for (;;) {
      const code = await openTotpPrompt({
        title,
        description: t("profile.totp.confirm_with_app"),
        errorMessage,
      });
      if (code === null) return; // user cancelled
      try {
        await settings.mutateAsync({ ...patch, code });
        return;
      } catch (err) {
        const e = err as { status?: unknown; detail?: unknown };
        // Server returns 428 + requires_totp on a wrong code; ask again.
        if (
          e.status === 428 &&
          typeof e.detail === "object" &&
          e.detail !== null &&
          (e.detail as { requires_totp?: unknown }).requires_totp === true
        ) {
          errorMessage = t("profile.totp.wrong_code.retry");
          continue;
        }
        push({ kind: "error", title: t("profile.totp.save_failed") });
        return;
      }
    }
  };

  const onToggleLogin = (next: boolean) =>
    askAndPatch(
      { require_login: next },
      next
        ? t("profile.totp.action.enable_login")
        : t("profile.totp.action.disable_login"),
    );
  const onTogglePurchase = (next: boolean) =>
    askAndPatch(
      { require_purchase: next },
      next
        ? t("profile.totp.action.enable_purchase")
        : t("profile.totp.action.disable_purchase"),
    );
  const onToggleDeposit = (next: boolean) =>
    askAndPatch(
      { require_deposit_change: next },
      next
        ? t("profile.totp.action.enable_deposit")
        : t("profile.totp.action.disable_deposit"),
    );

  return (
    <section className="card p-4 sm:p-6">
      <h2 className="text-lg font-semibold text-slate-800">
        {t("profile.section.totp")}
      </h2>
      <p className="mt-1 text-sm text-slate-500">{t("profile.totp.subtitle")}</p>

      {status.isLoading && (
        <div className="mt-3 text-sm text-slate-400">{t("common.loading")}</div>
      )}

      {/* --- not enabled & no pending setup: ask for current password --- */}
      {!enabled && !pending && status.data && (
        <form onSubmit={onStart} className="mt-4 grid gap-3 sm:max-w-md">
          <div>
            <label className="label" htmlFor="setup_password">
              {t("profile.password.current")}
            </label>
            <input
              id="setup_password"
              type="password"
              autoComplete="current-password"
              className="input"
              value={setupPassword}
              onChange={(e) => setSetupPassword(e.target.value)}
              required
            />
            <div className="mt-1 text-xs text-slate-500">
              {t("profile.totp.password_hint")}
            </div>
          </div>
          <button
            type="submit"
            className="btn btn-primary text-sm sm:w-fit"
            disabled={setup.isPending || !setupPassword}
          >
            {setup.isPending
              ? t("profile.totp.generating")
              : t("profile.totp.enable_btn")}
          </button>
        </form>
      )}

      {/* --- pending: show secret + confirm code field --- */}
      {!enabled && pending && (
        <form onSubmit={onConfirm} className="mt-4 space-y-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t("profile.totp.step1.title")}
            </div>
            <p className="mt-1 text-xs text-slate-500">
              {t("profile.totp.step1.desc")}
            </p>
            <div className="mt-2 grid gap-3 sm:grid-cols-[auto_minmax(0,1fr)]">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=176x176&data=${encodeURIComponent(pending.otpauth_url)}`}
                alt={t("profile.totp.qr_alt")}
                className="h-44 w-44 rounded-md border border-surface-border bg-white p-1"
              />
              <div className="space-y-2">
                <div>
                  <div className="label">{t("profile.totp.secret_label")}</div>
                  <div className="font-mono text-sm text-slate-800 break-all">
                    {pending.secret}
                  </div>
                </div>
                <div className="text-xs text-slate-500">
                  {t("profile.totp.issuer_note")}
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t("profile.totp.step2.title")}
            </div>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              autoComplete="one-time-code"
              placeholder="123 456"
              className="input mt-1 text-center tracking-[0.4em]"
              value={confirmCode}
              onChange={(e) =>
                setConfirmCode(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              required
            />
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setPending(null);
                setConfirmCode("");
              }}
              className="btn btn-ghost text-sm"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              className="btn btn-primary text-sm"
              disabled={confirm.isPending || !/^\d{6}$/.test(confirmCode)}
            >
              {confirm.isPending
                ? t("profile.totp.confirming")
                : t("profile.totp.activate_btn")}
            </button>
          </div>
        </form>
      )}

      {/* --- enabled: toggles + disable form --- */}
      {enabled && status.data && (
        <div className="mt-4 space-y-4">
          <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
            {t("profile.totp.active")}
          </div>

          <div className="space-y-2">
            <ToggleRow
              label={t("profile.totp.toggle.login")}
              hint={t("profile.totp.toggle.login.hint")}
              checked={!!status.data.require_login}
              disabled={settings.isPending}
              onChange={onToggleLogin}
            />
            <ToggleRow
              label={t("profile.totp.toggle.purchase")}
              hint={t("profile.totp.toggle.purchase.hint")}
              checked={!!status.data.require_purchase}
              disabled={settings.isPending}
              onChange={onTogglePurchase}
            />
            <ToggleRow
              label={t("profile.totp.toggle.deposit")}
              hint={t("profile.totp.toggle.deposit.hint")}
              checked={!!status.data.require_deposit_change}
              disabled={settings.isPending}
              onChange={onToggleDeposit}
            />
          </div>

          <form
            onSubmit={onDisable}
            className="grid gap-2 border-t border-surface-border pt-3 sm:max-w-md sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end"
          >
            <div>
              <label className="label" htmlFor="disable_code">
                {t("profile.totp.disable_label")}
              </label>
              <input
                id="disable_code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                autoComplete="one-time-code"
                placeholder="123 456"
                className="input text-center tracking-[0.4em]"
                value={disableCode}
                onChange={(e) =>
                  setDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
              />
            </div>
            <button
              type="submit"
              className="btn btn-ghost text-sm text-red-600"
              disabled={disable.isPending || !/^\d{6}$/.test(disableCode)}
            >
              {disable.isPending
                ? t("profile.totp.disabling")
                : t("profile.totp.disable_btn")}
            </button>
          </form>
        </div>
      )}
    </section>
  );
}

function ToggleRow({
  label,
  hint,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  disabled: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-md border border-surface-border px-3 py-2.5 hover:bg-surface-muted">
      <input
        type="checkbox"
        className="mt-0.5"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <div className="min-w-0">
        <div className="text-sm font-medium text-slate-800">{label}</div>
        <div className="text-xs text-slate-500">{hint}</div>
      </div>
    </label>
  );
}
